import { useState } from "react";
import axios from "axios";
import { Sun, Instagram, Facebook, Send } from "lucide-react";
import { toast } from "sonner";
import { SITE, scrollToId } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list — specials and secret menus, first.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="relative overflow-hidden border-t border-terracotta/15 bg-clay-900" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-10 relative z-10">
        <div className="grid md:grid-cols-3 gap-12 mb-20">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-9 h-9 rounded-full bg-terracotta flex items-center justify-center">
                <Sun className="w-5 h-5 text-obsidian" strokeWidth={2.2} />
              </span>
              <span className="font-serif-d text-2xl font-semibold text-cream">Alejandra's</span>
            </div>
            <p className="text-sm text-cream/50 font-light leading-relaxed max-w-xs">
              Authentic Mexican cooking from our family to yours. {SITE.address}.
            </p>
            <div className="flex gap-3 mt-6">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-cream/15 flex items-center justify-center text-cream/60 hover:border-terracotta hover:text-terracotta transition-colors duration-300" data-testid="footer-facebook" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-cream/15 flex items-center justify-center text-cream/60 hover:border-terracotta hover:text-terracotta transition-colors duration-300" data-testid="footer-instagram" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-terracotta mb-6">Explore</h4>
            <div className="flex flex-col gap-3">
              {[
                { label: "Our Story", id: "story" },
                { label: "Menu", id: "menu" },
                { label: "Gallery", id: "gallery" },
                { label: "Reservations", id: "reserve" },
                { label: "Hours & Directions", id: "visit" },
              ].map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollToId(l.id)}
                  className="text-left text-sm text-cream/55 hover:text-terracotta transition-colors duration-300 font-light w-fit"
                  data-testid={`footer-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-terracotta mb-6">The VIP List</h4>
            <p className="text-sm text-cream/50 font-light mb-5">
              Secret menus, special offers and first word on family events.
            </p>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 bg-clay-800 border border-terracotta/15 rounded-full px-5 py-3 text-sm text-cream placeholder:text-cream/35 focus:outline-none focus:border-terracotta/60 transition-colors"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={sending}
                className="w-12 h-12 rounded-full bg-terracotta text-obsidian flex items-center justify-center hover:bg-terracotta-hover transition-colors duration-300 disabled:opacity-60 shrink-0"
                data-testid="newsletter-submit-btn"
                aria-label="Subscribe"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-terracotta/10">
          <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-cream/35" data-testid="footer-copyright">
            © {new Date().getFullYear()} Alejandra's Authentic Mexican Restaurant
          </p>
          <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-cream/35">
            Family owned · Aurora, Indiana
          </p>
        </div>
      </div>

      <div
        className="pointer-events-none select-none absolute -bottom-10 left-1/2 -translate-x-1/2 font-serif-d font-bold text-[22vw] leading-none text-stroke-terracotta opacity-40 whitespace-nowrap"
        aria-hidden="true"
      >
        ALEJANDRA'S
      </div>
    </footer>
  );
}
